import React from 'react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

export interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page?: number
}

interface PaginationProps {
  meta: PaginationMeta
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  pageSizeOptions?: number[]
  className?: string
  itemLabel?: string
  showPageSizeSelector?: boolean
}

export function getPageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages: (number | string)[] = [1]

  if (currentPage > 3) {
    pages.push('...')
  }

  const start = Math.max(2, currentPage - 1)
  const end = Math.min(totalPages - 1, currentPage + 1)

  for (let i = start; i <= end; i++) {
    if (!pages.includes(i)) pages.push(i)
  }

  if (currentPage < totalPages - 2) {
    pages.push('...')
  }

  if (!pages.includes(totalPages)) {
    pages.push(totalPages)
  }

  return pages
}

export function Pagination({
  meta,
  onPageChange,
  onPerPageChange,
  pageSizeOptions = [10, 20, 50, 100],
  className = '',
  itemLabel = 'items',
  showPageSizeSelector = true,
}: PaginationProps) {
  const currentPage = meta.current_page || 1
  const totalPages = Math.max(1, meta.total_pages || 1)
  const totalCount = meta.total_count || 0
  const perPage = meta.per_page || 10

  const fromItem = totalCount === 0 ? 0 : (currentPage - 1) * perPage + 1
  const toItem = Math.min(currentPage * perPage, totalCount)

  const pageNumbers = getPageNumbers(currentPage, totalPages)

  return (
    <div className={`space-y-2.5 sm:space-y-0 sm:flex sm:items-center sm:justify-between gap-3 text-xs sm:text-sm text-muted-foreground py-2 ${className}`}>
      {/* Top / Left: Summary & Page Size Selector */}
      <div className="flex items-center justify-between sm:justify-start gap-3 w-full sm:w-auto">
        <span className="text-xs sm:text-sm">
          Showing <span className="font-semibold text-foreground">{fromItem}–{toItem}</span> of{' '}
          <span className="font-semibold text-foreground">{totalCount.toLocaleString()}</span> {itemLabel}
        </span>

        {showPageSizeSelector && (
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="hidden sm:inline text-xs text-muted-foreground whitespace-nowrap">Per page:</span>
            <Select
              value={String(perPage)}
              onValueChange={(val) => onPerPageChange(Number(val))}
            >
              <SelectTrigger className="h-8 w-[68px] sm:w-[72px] text-xs bg-background">
                <SelectValue placeholder={String(perPage)} />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)} className="text-xs">
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Mobile-only Compact Pager (<sm) */}
      <div className="flex sm:hidden items-center justify-between gap-2 pt-2 border-t border-border/50">
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs font-semibold gap-1 rounded-xl"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          <span>Prev</span>
        </Button>

        <span className="text-xs font-medium text-foreground">
          Page <strong className="font-bold">{currentPage}</strong> of <span className="text-muted-foreground">{totalPages}</span>
        </span>

        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs font-semibold gap-1 rounded-xl"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <span>Next</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Desktop Full Pagination Controls (sm:flex) */}
      <div className="hidden sm:flex items-center gap-1">
        {/* First Page button */}
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-lg"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(1)}
          title="First Page"
        >
          <ChevronsLeft className="h-4 w-4" />
          <span className="sr-only">First Page</span>
        </Button>

        {/* Previous Page button */}
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2.5 gap-1 text-xs rounded-lg"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          title="Previous Page"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Prev</span>
        </Button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((p, idx) => {
            if (typeof p === 'string') {
              return (
                <span key={`ellipsis-${idx}`} className="px-1 text-xs text-muted-foreground">
                  ...
                </span>
              )
            }

            const isCurrent = p === currentPage
            return (
              <Button
                key={p}
                variant={isCurrent ? 'default' : 'outline'}
                size="icon"
                className={`h-8 w-8 text-xs font-medium rounded-lg ${
                  isCurrent ? 'pointer-events-none' : ''
                }`}
                onClick={() => onPageChange(p)}
              >
                {p}
              </Button>
            )
          })}
        </div>

        {/* Next Page button */}
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-2.5 gap-1 text-xs rounded-lg"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          title="Next Page"
        >
          <span>Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Last Page button */}
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-lg"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(totalPages)}
          title="Last Page"
        >
          <ChevronsRight className="h-4 w-4" />
          <span className="sr-only">Last Page</span>
        </Button>
      </div>
    </div>
  )
}
